import { useState } from "react";
import { createPortal } from "react-dom";
import { KeyRound, Copy, Check } from "lucide-react";
import { toast } from "sonner";

export default function RecoveryCodeDialog({ code, onDone }) {
  const [saved, setSaved] = useState(false);
  const [copied, setCopied] = useState(false);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
      toast.success("Recovery code copied.");
    } catch {
      toast.info("Please write the code down.");
    }
  };

  return createPortal(
    <div className="fixed inset-0 z-[60] bg-black/80 backdrop-blur-sm flex items-center justify-center p-6" data-testid="recovery-code-dialog">
      <div className="w-full max-w-md border border-primary-gold/40 bg-[#121212] p-8 fade-up">
        <KeyRound className="w-6 h-6 text-primary-gold" />
        <h2 className="font-serif-display text-3xl mt-4">Save your recovery code</h2>
        <p className="text-soft text-sm mt-3 leading-relaxed">
          If you ever forget your password, this code lets you set a new one. It's shown only once, so keep it somewhere
          safe, such as a note on your phone.
        </p>
        <div className="mt-6 flex items-center justify-between gap-3 border border-white/15 bg-[#0B0C10] px-5 py-4">
          <span className="font-mono-ui text-lg sm:text-xl tracking-[0.12em] sm:tracking-[0.2em] whitespace-nowrap text-primary-gold" data-testid="recovery-code-value">{code}</span>
          <button onClick={copy} className="text-soft hover:text-primary-gold transition-colors" data-testid="recovery-code-copy" aria-label="Copy code">
            {copied ? <Check className="w-5 h-5" /> : <Copy className="w-5 h-5" />}
          </button>
        </div>
        <label className="mt-6 flex items-center gap-3 text-sm cursor-pointer">
          <input type="checkbox" checked={saved} onChange={(e) => setSaved(e.target.checked)} className="accent-[#D4AF37] w-4 h-4" data-testid="recovery-code-saved-checkbox" />
          I've saved my recovery code
        </label>
        <button
          disabled={!saved}
          onClick={onDone}
          data-testid="recovery-code-continue"
          className="mt-6 w-full bg-primary-gold text-[#0B0C10] font-mono-ui text-xs uppercase tracking-[0.2em] py-4 disabled:opacity-40 transition-opacity"
        >
          Continue
        </button>
      </div>
    </div>,
    document.body
  );
}
